import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * Unified EmptyState component for consistent empty state messaging across the app.
 *
 * Supports optional onboarding hints to help guide new users toward actions.
 *
 * @param {string} emoji - Large emoji to display (e.g., '📭')
 * @param {string} title - Main empty state message
 * @param {string} description - Secondary description text
 * @param {React.ReactNode} action - Optional action button or link
 * @param {object} hint - Optional onboarding hint object { text, actionText, onAction }
 * @param {string} size - Size variant: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} className - Additional classes for the container
 * @param {React.ReactNode} children - Optional additional content
 */
function EmptyState({
  emoji,
  icon,
  title,
  description,
  action,
  hint,
  size = 'md',
  className = '',
  children
}) {
  // Size-based styling
  const sizeStyles = {
    sm: {
      container: 'py-6',
      emoji: 'text-4xl mb-2',
      icon: 'w-10 h-10 mb-2',
      title: 'text-base font-medium mb-1',
      description: 'text-sm mb-3'
    },
    md: {
      container: 'py-12',
      emoji: 'text-6xl mb-4',
      icon: 'w-16 h-16 mb-4',
      title: 'text-lg font-medium mb-2',
      description: 'text-sm mb-4'
    },
    lg: {
      container: 'py-16',
      emoji: 'text-7xl mb-6',
      icon: 'w-20 h-20 mb-6',
      title: 'text-xl font-semibold mb-3',
      description: 'text-base mb-6'
    }
  };

  const styles = sizeStyles[size];

  return (
    <div className={`text-center ${styles.container} ${className}`}>
      {/* Emoji or Icon */}
      {emoji && (
        <div className={styles.emoji} role="img" aria-hidden="true">
          {emoji}
        </div>
      )}
      {icon && !emoji && (
        <div className={`${styles.icon} mx-auto text-content-muted`}>
          {icon}
        </div>
      )}

      {/* Title */}
      {title && (
        <h4 className={`${styles.title} text-content-primary`}>
          {title}
        </h4>
      )}

      {/* Description */}
      {description && (
        <p className={`${styles.description} text-content-muted max-w-md mx-auto`}>
          {description}
        </p>
      )}

      {/* Action Button */}
      {action && (
        <div className="mb-4">
          {action}
        </div>
      )}

      {/* Additional Children */}
      {children}

      {/* Onboarding Hint */}
      {hint && (
        <OnboardingHint
          text={hint.text}
          actionText={hint.actionText}
          onAction={hint.onAction}
          variant={hint.variant || 'info'}
        />
      )}
    </div>
  );
}

/**
 * Onboarding hint component shown within empty states
 */
function OnboardingHint({ text, actionText, onAction, variant = 'info' }) {
  const variantStyles = {
    info: 'bg-info-light border-info text-info-dark',
    tip: 'bg-warning-light border-warning text-warning-dark',
    success: 'bg-success-light border-success text-success-dark'
  };

  const iconStyles = {
    info: <Icon name="infoCircle" className="w-4 h-4 mr-2 flex-shrink-0" />,
    tip: <Icon name="lightbulb" className="w-4 h-4 mr-2 flex-shrink-0" />,
    success: <Icon name="checkCircle" className="w-4 h-4 mr-2 flex-shrink-0" />
  };

  return (
    <div className={`mt-4 mx-auto max-w-sm p-3 rounded-lg border ${variantStyles[variant]}`}>
      <div className="flex items-start text-left">
        {iconStyles[variant]}
        <div className="flex-1">
          <p className="text-sm">{text}</p>
          {actionText && onAction && (
            <button
              onClick={onAction}
              className="mt-2 text-sm font-medium underline hover:no-underline"
            >
              {actionText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Preset empty states for common scenarios
 */
EmptyState.NoResults = function NoResults({ searchTerm, onClearFilters }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="search" />}
      title={t('emptyStates.noResults.title')}
      description={searchTerm
        ? t('emptyStates.noResults.descriptionWithTerm', { searchTerm })
        : t('emptyStates.noResults.descriptionDefault')
      }
      action={onClearFilters && (
        <button
          onClick={onClearFilters}
          className="text-primary hover:text-primary-dark font-medium"
        >
          {t('emptyStates.noResults.clearFiltersButton')}
        </button>
      )}
    />
  );
};

EmptyState.NoSavedItems = function NoSavedItems({ onExplore }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="bookmarkOutline" />}
      title={t('emptyStates.noSavedItems.title')}
      description={t('emptyStates.noSavedItems.description')}
      hint={{
        text: t('emptyStates.noSavedItems.hintText'),
        variant: 'tip'
      }}
      action={onExplore && (
        <button
          onClick={onExplore}
          className="px-4 py-2 bg-primary text-content-inverse rounded-lg hover:bg-primary-dark transition-colors"
        >
          {t('emptyStates.noSavedItems.exploreButton')}
        </button>
      )}
    />
  );
};

EmptyState.NoTrips = function NoTrips({ onCreateTrip }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="airplane" />}
      title={t('emptyStates.noTrips.title')}
      description={t('emptyStates.noTrips.description')}
      hint={{
        text: t('emptyStates.noTrips.hintText'),
        variant: 'tip'
      }}
      action={onCreateTrip && (
        <button
          onClick={onCreateTrip}
          className="px-4 py-2 bg-primary text-content-inverse rounded-lg hover:bg-primary-dark transition-colors"
        >
          {t('emptyStates.noTrips.planTripButton')}
        </button>
      )}
    />
  );
};

EmptyState.NoCollections = function NoCollections({ onCreateCollection }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="folder" />}
      title={t('emptyStates.noCollections.title')}
      description={t('emptyStates.noCollections.description')}
      hint={{
        text: t('emptyStates.noCollections.hintText'),
        variant: 'info'
      }}
      action={onCreateCollection && (
        <button
          onClick={onCreateCollection}
          className="px-4 py-2 bg-primary text-content-inverse rounded-lg hover:bg-primary-dark transition-colors"
        >
          {t('emptyStates.noCollections.createButton')}
        </button>
      )}
    />
  );
};

EmptyState.NoActivities = function NoActivities({ dayLabel }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="calendar" />}
      title={dayLabel ? t('emptyStates.noActivities.titleWithDay', { dayLabel }) : t('emptyStates.noActivities.titleDefault')}
      description={t('emptyStates.noActivities.description')}
      size="sm"
    />
  );
};

EmptyState.NoNotifications = function NoNotifications() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="bell" />}
      title={t('emptyStates.noNotifications.title')}
      description={t('emptyStates.noNotifications.description')}
      size="sm"
    />
  );
};

EmptyState.NoFriends = function NoFriends({ onInviteFriends }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="userGroup" />}
      title={t('emptyStates.noFriends.title')}
      description={t('emptyStates.noFriends.description')}
      hint={{
        text: t('emptyStates.noFriends.hintText'),
        variant: 'tip'
      }}
      action={onInviteFriends && (
        <button
          onClick={onInviteFriends}
          className="px-4 py-2 bg-primary text-content-inverse rounded-lg hover:bg-primary-dark transition-colors"
        >
          {t('emptyStates.noFriends.findFriendsButton')}
        </button>
      )}
    />
  );
};

EmptyState.Error = function ErrorState({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<Icon name="warning" />}
      title={t('emptyStates.error.title')}
      description={message || t('emptyStates.error.descriptionFallback')}
      action={onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 border border-border rounded-lg hover:bg-background transition-colors"
        >
          {t('emptyStates.error.retryButton')}
        </button>
      )}
    />
  );
};

export default EmptyState;
